/**
 * AOI Page
 *
 * 
 */

import { useEffect, useState, useMemo } from 'react';
import { ChevronDown, Search, Plus, Edit, Trash2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import MapViewer from '@/features/map/components/CesiumViewer';
import { MapControls } from '@/features/map/components/MapControls';
import { Viewer as CesiumViewer } from 'cesium';
import { useNavigate } from 'react-router-dom';
import { useAOIListQuery, useDeleteAOIMutation } from '@/features/aoi/api/aoi.api';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';

type AOI = {
    aoiId: string;
    aoiName: string;
    description?: string;
};

const MemoizedMapViewer = React.memo(MapViewer);

export function AreaInterestPage() {
    const [filters, setFilters] = useState({
        filter1: false,
        filter2: false,
        filter3: false,
        filter4: false
    });

    const navigate = useNavigate();
    const [viewer, setViewer] = useState<CesiumViewer | undefined>();
    const [selectedMapType, setSelectedMapType] = useState('1');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [aoiToDelete, setAoiToDelete] = useState<string | null>(null);
    const deleteMutation = useDeleteAOIMutation();
    // Add this handler function
    const handleDeleteClick = (aoiId: string) => {
        setAoiToDelete(aoiId);
        setDeleteDialogOpen(true);
    };

    // Use a debounced search term to avoid too many API calls
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(0); // Reset to first page on new search
        }, 500);
        return () => clearTimeout(timerId);
    }, [searchQuery]);

    const { data: aoiData } = useAOIListQuery(
        currentPage,
        pageSize,
        debouncedSearch
    );

    const toggleFilter = (filterKey: keyof typeof filters) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: !prev[filterKey]
        }));
    };

    const handleViewerReady = (cesiumViewer: CesiumViewer) => {
        setViewer(cesiumViewer);
    };

    const handleMapTypeChange = (mapType: string) => {
        setSelectedMapType(mapType);
    };

    // Search is now handled by the useEffect with debounce

    // const handlePageChange = (page: number) => {
    //     setCurrentPage(page);
    // };

    const aoiItems = aoiData?.content || [];
    const totalPages = aoiData?.totalPages || 0;
    const totalElements = aoiData?.totalElements || 0;

    const columns: ColumnDef<AOI>[] = [
        {
            accessorKey: 'aoiName',
            header: 'Name',
            cell: info => (
                <div className="text-white font-medium truncate">
                    {info.getValue() as string}
                </div>
            ),
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <div className="flex justify-end items-center gap-3">
                    <button
                        onClick={() => navigate(`/mission-planning/area-of-interest/form/${row.original.aoiId}`)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        title="Edit"
                    >
                        <Edit size={16} />
                    </button>
                    <button
                        onClick={() => { handleDeleteClick(row.original.aoiId) }}
                        className="p-1.5 rounded-full hover:bg-slate-700 text-red-500 hover:text-red-400 transition-colors"
                        title="Delete"
                    // disabled={deleteMutation.isPending}
                    >
                        {/* {deleteMutation.isPending && aoiToDelete === row.original.aoiId ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : ( */}
                        <Trash2 size={16} />
                        {/* )} */}
                    </button>
                    <button
                        className="text-slate-400 hover:text-slate-300 transition-colors"
                        title="Info"
                    >
                        <Info size={16} />
                    </button>
                </div>
            ),
        },
    ];

    const table = useReactTable({
        data: aoiItems,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        pageCount: totalPages,
        manualPagination: true,
        state: {
            pagination: {
                pageIndex: currentPage,
                pageSize: pageSize,
            },
        },
        onPaginationChange: (updater) => {
            const newPage = typeof updater === 'function'
                ? updater({ pageIndex: currentPage, pageSize })
                : updater;
            setCurrentPage(newPage.pageIndex);
        },
    });

    const mapViewerProps = useMemo(() => ({
        onViewerReady: handleViewerReady,
        selectedMapType: selectedMapType
    }), [selectedMapType]);

    const mapControlsProps = useMemo(() => ({
        viewer: viewer,
        selectedMapType: selectedMapType,
        onMapTypeChange: handleMapTypeChange,
    }), [viewer, selectedMapType, handleMapTypeChange]);

    // Add these functions inside your component, after the state declarations
    const handleCancelDelete = () => {
        console.log('Cancel button clicked');
        setDeleteDialogOpen(false);
        setAoiToDelete(null);
    };

    const confirmDelete = async () => {
        if (!aoiToDelete) return;

        try {
            await deleteMutation.mutateAsync(aoiToDelete);
            setDeleteDialogOpen(false);
            setAoiToDelete(null);
            // Optionally add a success toast here
        } catch (error) {
            console.error('Failed to delete AOI:', error);
            // Optionally add an error toast here
        }
    };

    return (
        <div className="absolute inset-0 flex flex-col pointer-events-none">
            {/* Map Panel */}
            <div
                className="absolute left-4 pointer-events-auto bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl overflow-hidden flex flex-col"
                style={{
                    top: 'calc(5rem)',
                    right: 'calc(1rem)',
                    bottom: 'calc(50% + 1rem)'
                }}
            >
                <div className="flex-1 relative bg-slate-800 rounded overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900">
                        <MemoizedMapViewer {...mapViewerProps} />
                        <MapControls {...mapControlsProps} />
                    </div>
                </div>
            </div>

            {/* Bottom Panel - Filters and Table */}
            <div
                className="absolute bottom-4 left-4 h-[48%] pointer-events-auto"
                style={{ right: 'calc(1rem)' }}
            >
                <div className={cn(
                    'h-full overflow-hidden flex flex-col'
                )}
                >
                    {/* Search, Filters and Create Asset Button */}
                    <div className='flex flex-col gap-3 py-3'>
                        <div className='flex flex-row justify-between items-center gap-4'>
                            <div className="flex items-center gap-4">
                                <div className="relative flex items-center">
                                    <Search size={16} className="absolute left-3 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search AOI by name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10 pr-4 py-1.5 w-64 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center gap-3">
                                    {[1, 2, 3, 4].map((num) => (
                                        <button
                                            key={num}
                                            onClick={() => toggleFilter(`filter${num}` as keyof typeof filters)}
                                            className={cn(
                                                'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                                                filters[`filter${num}` as keyof typeof filters]
                                                    ? 'bg-slate-700 text-white'
                                                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                                            )}
                                        >
                                            <div className="w-3 h-3 bg-slate-600 rounded"></div>
                                            <span>Filter {num}</span>
                                            <ChevronDown size={14} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Create Asset Button */}
                            <button
                                onClick={() => navigate('/mission-planning/area-of-interest/form')}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                            >
                                <Plus size={16} />
                                <span>New AOI</span>
                            </button>
                        </div>
                    </div>

                    {/* Table Container */}
                    <div className="flex-1 overflow-hidden flex flex-col bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-0 bg-slate-800 border-t border-slate-700 px-4 py-3 text-xs font-semibold text-slate-300">
                            <div className="col-span-8">Name</div>
                            <div className="col-span-4 text-right">Actions</div>
                        </div>
                        {/* Table Body */}
                        <div className="flex-1 overflow-y-auto">
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map(row => (
                                    <div
                                        key={row.id}
                                        className="grid grid-cols-12 gap-0 border-b border-slate-800 px-4 py-3 text-sm text-slate-300 hover:bg-slate-800/50 transition-colors items-center"
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <div
                                                key={cell.id}
                                                className={cell.column.id === 'actions' ? 'col-span-4' : 'col-span-8'}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </div>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                <div className="flex items-center justify-center h-full text-slate-400">
                                    <span>No AOI data available</span>
                                </div>
                            )}
                        </div>

                        {/* Footer / Pagination */}
                        <div className="px-4 py-3 flex items-center justify-between text-xs text-slate-400">
                            <span>
                                Showing {table.getRowModel().rows.length > 0 ? currentPage * pageSize + 1 : 0} to{' '}
                                {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} items
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                    className="px-2 py-1 hover:bg-slate-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <span className="flex items-center gap-1">
                                    <div>Page</div>
                                    <strong>
                                        {currentPage + 1} of {table.getPageCount()}
                                    </strong>
                                </span>
                                <button
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                    className="px-2 py-1 hover:bg-slate-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {deleteDialogOpen && (
                <Dialog.Root open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <Dialog.Portal>
                        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
                        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-800 p-6 rounded-lg max-w-md w-full mx-4 z-50">
                            <Dialog.Title className="text-lg font-medium text-white mb-4">Delete AOI</Dialog.Title>
                            <Dialog.Description className="text-slate-300 mb-6">
                                Are you sure you want to delete this AOI? This action cannot be undone.
                            </Dialog.Description>
                            <div className="flex justify-end gap-3">
                                <Dialog.Close asChild>
                                    <button
                                        className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white"
                                        onClick={handleCancelDelete}
                                    >
                                        Cancel
                                    </button>
                                </Dialog.Close>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                                    disabled={deleteMutation.isPending}
                                >
                                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </Dialog.Content>
                    </Dialog.Portal>
                </Dialog.Root>
            )}
        </div>
    );
}